/**
 * Plain-language text for the stock page summary. Pure functions of data
 * the page already has, so the server and the hydrating browser produce the
 * same text.
 */

const SENTENCE_END = /(?<=[.!?])\s+(?=[A-Z0-9"“(])/;

/**
 * The first sentence or two of a company description, at most `maxLength`
 * characters. Whole sentences only; a single sentence that's too long is cut
 * on a word boundary with an ellipsis.
 */
export const descriptionExcerpt = (
  description: string | null | undefined,
  maxLength = 300,
): string => {
  const text = (description ?? "").replace(/\s+/g, " ").trim();
  if (!text) return "";

  const sentences = text.split(SENTENCE_END);
  let excerpt = "";
  for (const sentence of sentences.slice(0, 2)) {
    const next = excerpt ? `${excerpt} ${sentence}` : sentence;
    if (next.length > maxLength) break;
    excerpt = next;
  }
  if (excerpt) return excerpt;

  const first = sentences[0];
  const clipped = first.slice(0, maxLength);
  // Keep the last word if the cut falls right after it; otherwise drop the
  // partial word.
  const endsOnWord = /\s/.test(first.charAt(maxLength));
  const lastSpace = clipped.lastIndexOf(" ");
  const cut = endsOnWord || lastSpace <= maxLength / 2 ? clipped : clipped.slice(0, lastSpace);
  return `${cut.replace(/[,;:\s]+$/, "")}…`;
};

/** Keys of the screens a stock doesn't pass, in display order. */
export const failingScreens = <K extends string>(
  screens: readonly { key: K; pass: boolean }[],
): K[] => screens.filter((screen) => !screen.pass).map((screen) => screen.key);
